import { SaveOutlined } from '@ant-design/icons';
import { Form, Input, Select, Switch } from 'antd';
import SubmitFormButton from 'common/components/submit-form-button.component';
import UserService from 'config/services/user.service';
import { CountryLocation } from 'config/types/user.type';
import AuthIntentUtils from 'config/utils/auth-intent.util';
import { isAuthError } from 'config/utils/errors.util';
import GlobalUtils from 'config/utils/globals.util';
import { SnackbarUtilities } from 'config/utils/snackbar.util';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { RootState } from 'store/index';
import { Address } from 'store/models/user.model';
import { SettingSlice } from 'store/slices/setting.slice';
import { UserSlice } from 'store/slices/user.slice';
import { AddressTranscription, GeneralTranscription, UserTranscription } from '../../../../config/constants/transcription.constant';
import {
  IComunaStateCLSelectOption,
  blockNumberFieldRules,
  neighborhoodFieldRules,
  stateFieldRules,
  streetFieldRules,
  streetNumberFieldRules,
} from '../../../../config/utils/address.util';
import AddressFormSkeleton from './skeleton';

type AddressFormProps = {
  address?: Address;
  callBack?: (value: any) => void;
};

const AddressForm: React.FC<AddressFormProps> = ({ address, callBack }) => {
  const [statesClData, setStateClData] = useState<Array<IComunaStateCLSelectOption>>([]);
  const [comunaClData, setComunaClData] = useState<Array<IComunaStateCLSelectOption>>([]);
  const [stateCode, setStateCode] = useState<string>('');
  const [neighborhoodCode, setNeighborhoodCode] = useState<string>('');

  const {
    setting: { countryLocations, isFetching: countryLocationsFetching },
  } = useSelector((state: RootState) => state);

  const [addressForm] = Form.useForm<Address>();
  const dispatch: Dispatch<AnyAction> = useDispatch();

  const handleCountryStates = (countryLocations: CountryLocation[]) => {
    const countryStates = countryLocations.map(({ name, code }) => ({ value: name, label: name, code }));
    setStateClData(countryStates);
  };

  const onStateChange = (regionValue: string) => {
    const countryState = countryLocations.find((countryLocation) => countryLocation.name === regionValue);
    setStateCode(countryState?.code || '');
    const countryStateComunas = countryState?.neighborhoods.map<IComunaStateCLSelectOption>(({ name, code }) => ({
      value: name,
      label: name,
      code,
    }));
    setComunaClData(countryStateComunas || []);
  };

  const handleSubmit = async (formValues: Address): Promise<void> => {
    await AuthIntentUtils.withAuthRetry(async () => {
      dispatch(UserSlice.fetchingRefresh(true));
      try {
        await UserService.createUserAddress({ ...formValues, stateCode, neighborhoodCode });
        dispatch(UserSlice.refreshUserProfile(await UserService.getUserProfile()));
        SnackbarUtilities.success(AddressTranscription.ADDRESS_SAVED_SUCCESSFULLY);
        callBack?.(false);
      } catch (error) {
        if (isAuthError(error)) throw error;
        SnackbarUtilities.error(UserTranscription.ERROR_FETCHING_USER_PROFILE);
      } finally {
        dispatch(UserSlice.fetchingRefresh(false));
      }
    });
  };

  const handleGetCountryLocationsFromSettings = async (): Promise<void> => {
    dispatch(SettingSlice.fetchingRefresh(true));
    try {
      const { countryLocations } = await UserService.getCountryLocationsFromSettings();
      handleCountryStates(countryLocations);
      dispatch(SettingSlice.refreshCountryLocations(countryLocations));
    } catch (error) {
      SnackbarUtilities.error(AddressTranscription.ERROR_FETCHING_LOCATIONS);
    } finally {
      dispatch(SettingSlice.fetchingRefresh(false));
    }
  };

  useEffect(() => {
    handleGetCountryLocationsFromSettings();
  }, []);

  return (
    <React.Fragment>
      {countryLocationsFetching ?
        <AddressFormSkeleton />
      : <Form form={addressForm} name='address-form' layout='vertical' autoComplete='off' initialValues={address} onFinish={handleSubmit}>
          <Form.Item
            label={`${AddressTranscription.STATE} ${GeneralTranscription.MANDATORY}`}
            name='state'
            rules={stateFieldRules}
            validateTrigger='onBlur'
          >
            <Select
              showSearch
              placeholder={AddressTranscription.STATE_PLACEHOLDER}
              optionFilterProp='children'
              onChange={onStateChange}
              filterOption={GlobalUtils.onFilterOption}
              options={statesClData}
            />
          </Form.Item>
          <Form.Item
            label={`${AddressTranscription.COMUNA} ${GeneralTranscription.MANDATORY}`}
            name='neighborhood'
            rules={neighborhoodFieldRules}
            validateTrigger='onBlur'
          >
            <Select
              showSearch
              placeholder={AddressTranscription.COMUNA_PLACEHOLDER}
              optionFilterProp='children'
              filterOption={GlobalUtils.onFilterOption}
              options={comunaClData}
              onChange={(_, option: any) => setNeighborhoodCode(option.code)}
            />
          </Form.Item>
          <Form.Item
            hasFeedback
            label={`${AddressTranscription.STREET} ${GeneralTranscription.MANDATORY}`}
            name='street'
            validateFirst
            rules={streetFieldRules}
          >
            <Input placeholder={AddressTranscription.STREET_PLACEHOLDER} />
          </Form.Item>
          <Form.Item
            hasFeedback
            label={`${AddressTranscription.STREET_NUMBER} ${GeneralTranscription.MANDATORY}`}
            name='streetNumber'
            validateFirst
            rules={streetNumberFieldRules}
          >
            <Input placeholder={AddressTranscription.STREET_NUMBER_PLACEHOLDER} />
          </Form.Item>
          <Form.Item
            hasFeedback
            label={`${AddressTranscription.BLOCK_NUMBER} ${GeneralTranscription.MANDATORY}`}
            name='blockNumber'
            validateFirst
            rules={blockNumberFieldRules}
          >
            <Input placeholder={AddressTranscription.BLOCK_NUMBER_PLACEHOLDER} />
          </Form.Item>
          <Form.Item label={AddressTranscription.SET_AS_CURRENT_ADDRESS} valuePropName='checked' name='selected'>
            <Switch checkedChildren={GeneralTranscription.YES} unCheckedChildren={GeneralTranscription.NO} defaultChecked={false} />
          </Form.Item>
          <Form.Item>
            <SubmitFormButton form={addressForm} text={AddressTranscription.SAVE_ADDRESS} icon={<SaveOutlined />} />
          </Form.Item>
        </Form>
      }
    </React.Fragment>
  );
};
export default AddressForm;
